import { FC, useMemo } from 'react';
import { RenderSearchLoading } from '../Loading';
import { EmptySearch } from '../EmptySearch';
import { ExerciseContent } from '../../../../core/components/organism/ContentCard/components/exerciseContent';
import { LessonContent } from '../../../../core/components/organism/ContentCard/components/lessonContent';
import { MaterialContent } from '../../../../core/components/organism/ContentCard/components/materialContent';
import { PodcastContent } from '../../../../core/components/organism/ContentCard/components/podcastContent';
import { Colors } from '../../../../core/models/disciplines';
import { categoryIcon } from '../../../Favorites/utils/card';
import { ISearchResult } from '../../models';
import { searchStore } from '../../stores/searchStore';
import { searchFilterStore } from '../../stores/searchFilters';
import { categoryURL, convertType } from '../../utils';
import ContentCard from '../../../../core/components/organism/ContentCard';
import { useNavigate } from 'react-router-dom';
import { Typography } from '../../../../core/components/molecules/Typography';
import { useExerciseStore } from '../../../Disciplines/stores/exercisesStore';
import { useNewSubjectStore } from '../../../Disciplines/stores/newSubjectStore';

interface IRenderSearchContentProps {
  callback: () => void;
  loading: boolean;
  selectedType?: string | null;
}

export const RenderSearchContent: FC<IRenderSearchContentProps> = ({
  callback,
  loading,
  selectedType,
}) => {
  const navigate = useNavigate();

  const { searchResult } = searchStore();
  // Termo que gerou a lista na tela: só é gravado quando a busca responde.
  const { activeSearchString } = searchFilterStore();
  const { clearFilters } = useExerciseStore();
  const { resetSubjects } = useNewSubjectStore();

  // A busca leva a um exercício específico pelo slug, mas a página de exercício
  // carrega a lista da subárea aplicando os filtros persistidos (assunto,
  // dificuldade, respondidos, etc.). Se o exercício buscado não passar por esses
  // filtros, ele some da lista e o usuário é redirecionado para o primeiro item.
  // Ao navegar a partir da busca zeramos os filtros para garantir que o
  // exercício correto esteja presente.
  const handleNavigate = (it: ISearchResult, url: string) => {
    if (it.type === 'exercise') {
      clearFilters();
      resetSubjects();
    }

    navigate(url);
  };

  const filteredSearchResult = useMemo(() => {
    const grouppedByType: {
      [key: string]: {
        type: string;
        items: ISearchResult[];
      };
    } = {};

    searchResult.forEach((it) => {
      if (grouppedByType[it.type]) {
        grouppedByType[it.type].items.push(it);
      } else {
        grouppedByType[it.type] = {
          type: it.type,
          items: [it],
        };
      }
    });

    const grouped = Object.values(grouppedByType);

    if (selectedType) {
      return grouped.filter((group) => group.type === selectedType);
    }

    return grouped;
  }, [searchResult, selectedType]);

  const renderCardContent = (it: ISearchResult) => {
    if (it.type === 'podcast') {
      return <PodcastContent time={it.duration || '--:--'} />;
    }

    if (it.type === 'workbook' || it.type === 'mindmap') {
      return <MaterialContent type={it.type || ''} />;
    }

    if (it.type === 'exercise') {
      return (
        <ExerciseContent
          text={it.statement || ''}
          highlight={activeSearchString}
        />
      );
    }

    if (it.type === 'lesson') {
      const time = it?.videoDuration || it?.duration || '--:--';
      return <LessonContent time={time} teacher={it.teacher || null} />;
    }
  };

  if (loading) {
    return <RenderSearchLoading />;
  }

  if (searchResult.length === 0) {
    return <EmptySearch />;
  }

  const formatNameType = (type: string) => {
    const types = {
      exercise: 'Exercício',
      lesson: 'Aula',
      workbook: 'Material',
      podcast: 'Podcast',
    };

    return types[type as keyof typeof types] || type;
  };

  return (
    <div className='flex flex-col gap-lg'>
      {filteredSearchResult.map((item, i) => {
        return (
          <>
            <div key={i}>
              <Typography className='text-h4 font-bold uppercase text-mono-color-dark'>
                {formatNameType(item.type)}
              </Typography>
            </div>
            {item.items.map((it, i) => {
              const url = categoryURL(it);

              return (
                <ContentCard
                  key={i}
                  color={
                    (it.discipline?.slug as Colors) || ('biologia' as Colors)
                  }
                  content={convertType(it.type)}
                  title={it.type === 'exercise' ? undefined : it.title}
                  favorite={it.favorite}
                  done={it.type === 'exercise' ? it.seen : it.done}
                  icon={categoryIcon(it?.type)}
                  iconColor='text-brand-color-secondary-pure'
                  action={() => handleNavigate(it, url)}
                  id={it.id}
                  discipline={it?.discipline?.title}
                  disciplineSlug={it?.discipline?.slug}
                  hasCallback={true}
                  callback={() => callback()}
                >
                  {renderCardContent(it)}
                </ContentCard>
              );
            })}
          </>
        );
      })}
    </div>
  );
};
